<template>
  <div class="fdd-wrap" :class="{ 'fdd-open': open, 'fdd-active': hasValue }" ref="trigger">
    <!-- Trigger button -->
    <button class="fdd-trigger" type="button" @click="toggle">
      <v-icon size="15" class="fdd-icon">mdi-filter-variant</v-icon>
      <span class="fdd-label">{{ selectedLabel }}</span>
      <v-icon size="15" class="fdd-chevron" :class="{ 'fdd-chevron-up': open }">
        mdi-chevron-down
      </v-icon>
    </button>

    <!-- Teleported panel rendered at body level to escape overflow:hidden -->
    <teleport-to-body v-if="open">
      <div
        class="fdd-panel"
        :style="panelStyle"
        ref="panel"
      >
        <ul class="fdd-list">
          <!-- "All" / clear option -->
          <li
            class="fdd-item"
            :class="{ 'fdd-item-active': !value || value === '' || value === null }"
            @click="select(null)"
          >
            <span class="fdd-item-text">{{ allLabel }}</span>
            <span v-if="!value || value === '' || value === null" class="fdd-dot"></span>
          </li>

          <!-- Options -->
          <li
            v-for="opt in options"
            :key="getVal(opt)"
            class="fdd-item"
            :class="{ 'fdd-item-active': value === getVal(opt) }"
            @click="select(getVal(opt))"
          >
            <span class="fdd-item-text">{{ getText(opt) }}</span>
            <span v-if="value === getVal(opt)" class="fdd-dot"></span>
          </li>
        </ul>
      </div>
    </teleport-to-body>
  </div>
</template>

<script>
// Minimal teleport-to-body for Vue 2
const TeleportToBody = {
  name: "TeleportToBody",
  render(h) {
    return h("div", this.$slots.default);
  },
  mounted() {
    document.body.appendChild(this.$el);
  },
  beforeDestroy() {
    if (this.$el && this.$el.parentNode) {
      this.$el.parentNode.removeChild(this.$el);
    }
  },
};

export default {
  name: "FilterDropdown",

  components: { TeleportToBody },

  model: {
    prop: "value",
    event: "change",
  },

  props: {
    value: { default: null },
    options: { type: Array, default: () => [] },
    itemText: { type: String, default: "name" },
    itemValue: { type: String, default: "id" },
    placeholder: { type: String, default: "Filter" },
    allLabel: { type: String, default: "All" },
    minWidth: { type: Number, default: 200 },
  },

  data() {
    return {
      open: false,
      panelStyle: {},
    };
  },

  computed: {
    hasValue() {
      return this.value !== null && this.value !== "" && this.value !== undefined;
    },

    selectedLabel() {
      if (!this.hasValue) return this.placeholder;
      const found = this.options.find((o) => this.getVal(o) === this.value);
      return found ? this.getText(found) : this.placeholder;
    },
  },

  methods: {
    getText(opt) {
      return typeof opt === "object" ? opt[this.itemText] : opt;
    },

    getVal(opt) {
      return typeof opt === "object" ? opt[this.itemValue] : opt;
    },

    toggle() {
      if (this.open) {
        this.close();
      } else {
        this.open = true;
        this.$nextTick(() => this.positionPanel());
      }
    },

    close() {
      this.open = false;
    },

    select(val) {
      this.$emit("change", val === null ? "" : val);
      this.close();
    },

    positionPanel() {
      const trigger = this.$refs.trigger;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const panelWidth = Math.max(this.minWidth, rect.width);

      // Right-align panel to trigger; flip if it would overflow viewport
      let left = rect.right - panelWidth;
      if (left < 8) left = rect.left;
      // Clamp to viewport
      if (left + panelWidth > window.innerWidth - 8) {
        left = window.innerWidth - panelWidth - 8;
      }

      this.panelStyle = {
        position: "fixed",
        top: rect.bottom + 8 + "px",
        left: left + "px",
        width: panelWidth + "px",
        zIndex: 9999,
      };
    },

    onOutsideClick(e) {
      const trigger = this.$refs.trigger;
      const panel = this.$refs.panel;
      if (
        trigger && !trigger.contains(e.target) &&
        panel && !panel.contains(e.target)
      ) {
        this.close();
      }
    },

    onScroll() {
      if (this.open) this.close();
    },
  },

  mounted() {
    document.addEventListener("mousedown", this.onOutsideClick);
    window.addEventListener("scroll", this.onScroll, true);
    window.addEventListener("resize", this.close);
  },

  beforeDestroy() {
    document.removeEventListener("mousedown", this.onOutsideClick);
    window.removeEventListener("scroll", this.onScroll, true);
    window.removeEventListener("resize", this.close);
  },
};
</script>

<style scoped>
/* ══════════════════════════════════════
   FILTER DROPDOWN — reusable component
══════════════════════════════════════ */
.fdd-wrap {
  position: relative;
  display: inline-block;
  flex-shrink: 0;
}

/* ── Trigger ── */
.fdd-trigger {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 38px;
  padding: 0 14px;
  min-width: 190px;
  background: var(--surface2, #f8fafc);
  border: 1.5px solid var(--border, #e2e8f0);
  border-radius: 10px;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s, box-shadow 0.2s;
  outline: none;
  font-family: inherit;
  white-space: nowrap;
}
.fdd-trigger:hover {
  border-color: var(--brand, #0d9972);
  background: var(--surface, #fff);
  box-shadow: 0 4px 14px rgba(13, 153, 114, 0.08);
}
.fdd-open .fdd-trigger {
  border-color: var(--brand, #0d9972);
  background: var(--surface, #fff);
  box-shadow: 0 0 0 3px rgba(13, 153, 114, 0.12);
}
.fdd-active .fdd-trigger {
  border-color: var(--brand, #0d9972);
}

.fdd-icon {
  color: var(--tx3, #9ca3af) !important;
  flex-shrink: 0;
  transition: color 0.2s;
}
.fdd-active .fdd-icon,
.fdd-open .fdd-icon {
  color: var(--brand, #0d9972) !important;
}
.fdd-label {
  flex: 1;
  text-align: left;
  font-size: 13px;
  font-weight: 700;
  color: var(--tx2, #374151);
  overflow: hidden;
  text-overflow: ellipsis;
  transition: color 0.2s;
}
.fdd-active .fdd-label,
.fdd-open .fdd-label {
  color: var(--brand, #0d9972) !important;
}
.fdd-chevron {
  color: var(--tx3, #9ca3af) !important;
  flex-shrink: 0;
  transition: transform 0.22s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.fdd-chevron-up {
  transform: rotate(180deg);
}

/* ── Dark mode trigger ── */
.theme--dark .fdd-trigger {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
}
.theme--dark .fdd-trigger:hover,
.theme--dark .fdd-open .fdd-trigger {
  background: rgba(255, 255, 255, 0.08);
  border-color: var(--brand);
}
.theme--dark .fdd-label {
  color: var(--tx2, #cbd5e1);
}

/* ── Mobile compact ── */
@media (max-width: 600px) {
  .fdd-trigger {
    min-width: 0 !important;
    height: 36px !important;
    padding: 0 10px !important;
    font-size: 12.5px !important;
    max-width: 160px;
  }
  .fdd-label {
    font-size: 12px !important;
    max-width: 100px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>

<!-- Panel styles are global (not scoped) because the panel is teleported to body -->
<style>
.fdd-panel {
  background: var(--surface, #fff);
  border: 1px solid var(--border, #e2e8f0);
  border-radius: 16px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.1), 0 4px 16px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  padding: 8px;
  animation: fddIn 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.fdd-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-height: 280px;
  overflow-y: auto;
}

.fdd-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.15s;
  user-select: none;
}
.fdd-item:hover {
  background: rgba(13, 153, 114, 0.08);
}
.fdd-item:hover .fdd-item-text {
  color: var(--brand, #0d9972);
}
.fdd-item-text {
  font-size: 13.5px;
  font-weight: 600;
  color: var(--tx2, #374151);
}
.fdd-item-active {
  background: rgba(13, 153, 114, 0.1);
}
.fdd-item-active .fdd-item-text {
  color: var(--brand, #0d9972);
  font-weight: 700;
}
.fdd-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--brand, #0d9972);
  flex-shrink: 0;
}

/* Dark mode panel */
.theme--dark .fdd-panel {
  background: #1e293b;
  border-color: rgba(255, 255, 255, 0.1);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.4);
}
.theme--dark .fdd-item-text {
  color: #cbd5e1;
}
.theme--dark .fdd-item:hover {
  background: rgba(13, 153, 114, 0.12);
}
.theme--dark .fdd-item-active {
  background: rgba(13, 153, 114, 0.15);
}

@keyframes fddIn {
  from { opacity: 0; transform: translateY(-6px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
}
</style>
